import { useCallback, useMemo } from "react";
import { DeductionItem } from "@/core/entities/deduction/deduction/DeductionItem";
import { Button, Icon, Icons, Span, Table, TableColumnType, Tooltip } from "@ir/ui";
import { IColumnAlias } from "@/core/repositories/deduction";
import { fakeDeductionItems, fakeDeductionItemColumns, getColumnProps } from "./utils/ChargeBreakdownTable.utils";
import { useWorkspace } from "@/hooks/workspace";
import useExpandTableHeight from "./utils/ChargeBreakdownTableHeight";

export interface ChargeBreakdownTableProps {
  deductionId: number;
  deductionItems: DeductionItem[];
  deductionItemColumns: IColumnAlias[];
  isLoading: boolean;
  isExpanded: boolean;
  keywords: string[];
  currentPage: number;
  pageSize: number;
}

const ChargeBreakdownTable = ({
  deductionId,
  deductionItems,
  deductionItemColumns,
  isLoading,
  keywords,
  isExpanded,
  currentPage,
  pageSize,
}: ChargeBreakdownTableProps) => {
  const { settings: workspaceSettings } = useWorkspace();

  const expandTableHeight = useExpandTableHeight();

  const getColumns = useCallback(
    (deductionItemColumns: IColumnAlias[], keywords: string[]): TableColumnType<DeductionItem>[] => {
      const columns: TableColumnType<DeductionItem>[] = [];
      for (const column of deductionItemColumns) {
        const currentColumnProps = getColumnProps(keywords, workspaceSettings).find(prop => prop.dataIndex === column.key);

        if (currentColumnProps) {
          const updatedColumn = {
            ...currentColumnProps,
            title: column.title ?? currentColumnProps.title,
            dataIndex: column.key as keyof DeductionItem,
            key: column.key,
          };
          columns.push(updatedColumn);
        }
      }
      columns.unshift({
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 56,
        fixed: "left",
        verticalAlign: "top",
        cellRender: (_value, record, index) => {
          const rowNumber = (currentPage - 1) * pageSize + index + 1;
          if (!record?.isValidityFailed) {
            return rowNumber;
          }
          const tooltipContent = (record.validityRcas ?? [])
            .map(r => r.rcaDescription || r.code)
            .filter(Boolean)
            .join("; ");
          return (
            <Span className="d-inline-flex flex-align-center" style={{ gap: 4, whiteSpace: "nowrap" }}>
              {rowNumber}
              <Tooltip content={tooltipContent || "Validity failed"} placement="right">
                <Button noStyle style={{ cursor: "default", lineHeight: 1 }}>
                  <Icon
                    name={Icons.AlertCircle}
                    size="small"
                    color="warning"
                    style={{ display: "block", height: 14, width: 14, color: "#DC6803" }}
                    className="chargebreakdown-validity-failed-icon"
                  />
                </Button>
              </Tooltip>
            </Span>
          );
        },
      });
      return columns;
    },
    [currentPage, pageSize, workspaceSettings]
  );

  const columns = useMemo(() => {
    return getColumns(deductionItemColumns, keywords);
  }, [keywords, deductionItemColumns, getColumns]);

  if (!isLoading && (deductionItems.length === 0 || deductionItems[0].deductionId !== deductionId)) {
    return null;
  }

  return (
    <Table<DeductionItem>
      columns={isLoading ? fakeDeductionItemColumns : columns}
      dataSource={isLoading ? fakeDeductionItems : deductionItems}
      border="row"
      aria-label="Deduction Items Table"
      aria-labelledby="deduction-items-table"
      sticky={true}
      scroll={{ x: "100%", y: isExpanded ? expandTableHeight : 200 }}
      className="mb-0 chargebreakdown-table-container"
      rowClassName={(_key, record) => (record?.isValidityFailed ? "table-row--highlighted" : "")}
    />
  );
};

export default ChargeBreakdownTable;
